import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Reorder } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ConfirmModal from '../components/Shared/ConfirmModal';

const Settings = () => {
  const { 
    plans, setPlans, addPlan, removePlan,
    agencies, setAgencies, addAgency, removeAgency,
    tags, setTags, addTag, removeTag,
    clients, updateClient
  } = useAppContext();

  const [activeSettingsTab, setActiveSettingsTab] = useState('system'); // system, bulk-clients
  const [newPlan, setNewPlan] = useState({ name: '', hours: '', color: '#14b8a6' });
  const [newAgency, setNewAgency] = useState({ name: '', color: '#3b82f6' });
  const [newTag, setNewTag] = useState({ name: '', color: '#f59e0b' });
  const [savingId, setSavingId] = useState(null);
  const [syncedIds, setSyncedIds] = useState(new Set());
  
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null });

  const confirmDelete = () => {
    const { type, id } = deleteConfirm;
    if (type === 'plan') removePlan(id);
    if (type === 'agency') removeAgency(id);
    if (type === 'tag') removeTag(id);
    setDeleteConfirm({ show: false, type: null, id: null });
  };

  const handleBulkUpdate = async (clientId, field, value) => {
    setSavingId(clientId);
    try {
      const finalValue = (field === 'usedHours') ? parseFloat(value) || 0 : value;
      await updateClient(clientId, { [field]: finalValue });
      // Track that this client was successfully synced
      setSyncedIds(prev => new Set(prev).add(clientId));
    } catch (err) {
      console.error("Bulk update failed", err);
    } finally {
      setTimeout(() => setSavingId(null), 500);
    }
  };

  const getPlanHours = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan ? plan.monthlyHours : 0;
  };

  // Sort clients alphabetically for the bulk edit view
  const sortedClients = [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="space-y-8 pb-20">
      <header className="flex justify-between items-end">
        <div>
          <p className="text-teal-500 font-bold text-xs uppercase tracking-widest mb-1">Configuration</p>
          <h1 className="text-4xl font-black text-white">System Settings</h1>
        </div>
        <div className="flex bg-slate-900 p-1 rounded-2xl border border-slate-800">
          <button 
            onClick={() => setActiveSettingsTab('system')}
            className={`px-6 py-2 rounded-xl text-sm font-bold transition-all ${activeSettingsTab === 'system' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
          >
            General
          </button>
          <button 
            onClick={() => setActiveSettingsTab('bulk-clients')}
            className={`px-6 py-2 rounded-xl text-sm font-bold transition-all ${activeSettingsTab === 'bulk-clients' ? 'bg-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
          >
            Bulk Edit Hours
          </button>
        </div>
      </header>

      {activeSettingsTab === 'system' ? (
        <div className="space-y-12">
          {/* Plans Section */}
          <section className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <SafeIcon icon={FiIcons.FiPackage} className="text-teal-400" />
                Service Plans
              </h2>
              <form onSubmit={(e) => { e.preventDefault(); addPlan({ name: newPlan.name, monthlyHours: parseInt(newPlan.hours), color: newPlan.color }); setNewPlan({ name: '', hours: '', color: '#14b8a6' }); }} className="flex flex-wrap gap-2">
                <input placeholder="Plan Name" className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none min-w-[150px]" value={newPlan.name} onChange={e => setNewPlan({...newPlan, name: e.target.value})} required />
                <input type="number" placeholder="Hrs" className="w-20 bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none" value={newPlan.hours} onChange={e => setNewPlan({...newPlan, hours: e.target.value})} required />
                <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl px-2">
                   <input type="color" className="w-6 h-6 bg-transparent border-none cursor-pointer" value={newPlan.color} onChange={e => setNewPlan({...newPlan, color: e.target.value})} />
                </div>
                <button className="bg-teal-600 px-4 py-2 rounded-xl text-white hover:bg-teal-500 transition-colors font-bold flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiPlus} /> Add
                </button>
              </form>
            </div>
            
            <Reorder.Group axis="y" values={plans} onReorder={setPlans} className="space-y-3">
              {plans.map((plan) => (
                <Reorder.Item key={plan.id} value={plan} className="bg-slate-900 p-5 rounded-2xl border border-slate-800 flex items-center justify-between group cursor-grab active:cursor-grabbing hover:border-teal-500/30 transition-all">
                  <div className="flex items-center gap-6">
                    <SafeIcon icon={FiIcons.FiMenu} className="text-slate-700" />
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-10 rounded-full" style={{ backgroundColor: plan.color || '#14b8a6' }}></div>
                      <div>
                        <p className="font-bold text-slate-100">{plan.name}</p>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-0.5">{plan.monthlyHours} Hours Allocation</p>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setDeleteConfirm({ show: true, type: 'plan', id: plan.id })} 
                    className="p-2 text-slate-700 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                  >
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </section>

          {/* Agencies Section */}
          <section className="space-y-6">
            <div className="flex justify-between items-end">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <SafeIcon icon={FiIcons.FiBriefcase} className="text-teal-400" />
                Agencies
              </h2>
              <form onSubmit={(e) => { e.preventDefault(); addAgency(newAgency); setNewAgency({ name: '', color: '#3b82f6' }); }} className="flex gap-2">
                <input placeholder="Agency Name" className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none" value={newAgency.name} onChange={e => setNewAgency({...newAgency, name: e.target.value})} required />
                <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl px-2">
                   <input type="color" className="w-6 h-6 bg-transparent border-none cursor-pointer" value={newAgency.color} onChange={e => setNewAgency({...newAgency, color: e.target.value})} />
                </div>
                <button className="bg-teal-600 px-4 py-2 rounded-xl text-white hover:bg-teal-500 transition-colors font-bold"><SafeIcon icon={FiIcons.FiPlus} /></button>
              </form>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agencies.map((agency) => (
                <div key={agency.id} className="bg-slate-900 p-5 rounded-2xl border border-slate-800 flex items-center justify-between group hover:border-teal-500/30 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-4 h-4 rounded-full border border-white/10" style={{ backgroundColor: agency.color }}></div>
                    <span className="font-bold text-slate-100">{agency.name}</span>
                  </div>
                  <button 
                    onClick={() => setDeleteConfirm({ show: true, type: 'agency', id: agency.id })} 
                    className="p-2 text-slate-700 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                  >
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Tags Section */}
          <section className="space-y-6">
            <div className="flex justify-between items-end">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <SafeIcon icon={FiIcons.FiTag} className="text-teal-400" />
                System Tags
              </h2>
              <form onSubmit={(e) => { e.preventDefault(); addTag(newTag); setNewTag({ name: '', color: '#f59e0b' }); }} className="flex gap-2">
                <input placeholder="Tag Label" className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-2 text-sm focus:ring-1 focus:ring-teal-500 outline-none" value={newTag.name} onChange={e => setNewTag({...newTag, name: e.target.value})} required />
                <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl px-2">
                  <input type="color" className="w-6 h-6 bg-transparent border-none cursor-pointer" value={newTag.color} onChange={e => setNewTag({...newTag, color: e.target.value})} />
                </div>
                <button className="bg-teal-600 px-4 py-2 rounded-xl text-white hover:bg-teal-500 transition-colors font-bold"><SafeIcon icon={FiIcons.FiPlus} /></button>
              </form>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {tags.map((tag) => (
                <div key={tag.id} className="px-5 py-2.5 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-4 group hover:bg-slate-800 hover:border-teal-500/20 transition-colors">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tag.color }}></div>
                  <span className="text-sm font-bold text-slate-200">{tag.name}</span>
                  <button 
                    onClick={() => setDeleteConfirm({ show: true, type: 'tag', id: tag.id })} 
                    className="text-slate-600 hover:text-red-400 transition-colors"
                  >
                    <SafeIcon icon={FiIcons.FiX} />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : (
        <section className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-800/30">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Client Name</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Plan Total</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Used Hours</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center w-20">Sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {sortedClients.map(client => (
                  <tr key={client.id} className="hover:bg-slate-800/20 transition-colors">
                    <td className="px-6 py-3">
                      <input 
                        className="bg-transparent border-none text-white font-bold outline-none focus:ring-1 focus:ring-teal-500 rounded px-2 py-1 w-full"
                        defaultValue={client.name}
                        onBlur={(e) => e.target.value !== client.name && handleBulkUpdate(client.id, 'name', e.target.value)}
                      />
                    </td>
                    <td className="px-6 py-3 text-center">
                      <span className="text-slate-400 font-mono font-bold">
                        {getPlanHours(client.planId)} hrs
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex justify-center">
                        <input 
                          type="number"
                          step="0.1"
                          className="bg-slate-800 border border-slate-700 text-teal-400 font-mono font-bold outline-none focus:ring-1 focus:ring-teal-500 rounded-lg px-3 py-1.5 w-24 text-center"
                          defaultValue={client.usedHours || 0}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (val !== (client.usedHours || 0)) {
                              handleBulkUpdate(client.id, 'usedHours', val);
                            }
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-3 text-center">
                      {savingId === client.id ? (
                        <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin text-teal-500 mx-auto" />
                      ) : (
                        <SafeIcon 
                          icon={FiIcons.FiCheckCircle} 
                          className={`mx-auto transition-colors duration-500 ${syncedIds.has(client.id) ? 'text-teal-500' : 'text-slate-700/50'}`} 
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <ConfirmModal 
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, type: null, id: null })}
        onConfirm={confirmDelete}
        title={`Delete ${deleteConfirm.type?.toUpperCase()}?`}
        message={`Are you sure you want to remove this ${deleteConfirm.type}? This might affect existing clients associated with it.`}
      />
    </div>
  );
};

export default Settings;